/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["state"] }] */
import profileApiClient from '@/services/profile/profile-service';
import { setCSSCustomUserOrCompanyColors } from '@/util/set-css-custom-properties';
import {
	isDifferent,
	mapDreForStore,
	mapDreForSave,
} from './user-profile-helpers';
import { USER_PROFILE_RESPONSES, MODAL_TITLES } from './constants/responses';
import { ERROR_DETAILS } from '../../constants/error-details';

const state = {
	contactId: 0,
	userId: 0,
	firstName: '',
	lastName: '',
	middleName: '',
	preferredName: '',
	phoneNumber: '',
	phoneExtension: '',
	mobilePhoneNumber: '',
	email: '',
	fambYourProfileURL: '',
	websiteUrl: '',
	nmlsNumber: '',
	doingBusinessAs: '',
	profilePhoto: {
		imageFormat: '',
		imageUri: '',
		referenceId: '',
	},
	aboutLO: '',
	socialMediaDetails: [
		{
			type: 'FaceBook',
			value: '',
		},
		{
			type: 'Twitter',
			value: '',
		},
		{
			type: 'Instagram',
			value: '',
		},
		{
			type: 'LinkedIn',
			value: '',
		},
		{
			type: 'TikTok',
			value: '',
		},
	],
	licenses: [
		{
			licenseKey: '',
			licenseValue: '',
			expirationDate: '',
		},
	],
	brand: [
		{
			name: 'HeaderFontFamily',
			value: 'Arial',
		},
		{
			name: 'Color1',
			value: '',
		},
		{
			name: 'Color2',
			value: '',
		},
		{
			name: 'Color3',
			value: '',
		},
	],
	organizationId: [0],
	activeIndicator: false,
	isFirstTimeSetupCompleted: false,
	termsOfUse: true,
	complianceLogo: {
		imageFormat: '',
		imageUri: '',
		referenceId: '',
	},
	personalCompanyLogo: {
		imageFormat: '',
		imageUri: '',
		referenceId: '',
	},
	isAccountExecutive: false,
	roles: [],
	associatedBrokerShops: [],
	showDisclaimerNotification: false,
	realEstateBrokerDre: '',
	realEstateSalespersonDre: '',
	contactLicenses: {
		BrokerDreLicense: '',
		SalespersonDreLicense: '',
	},
	rolodexContactId: 0,
	rolodexModifiedIndicator: false,
	saveUserInCCV2: false,
	blinkRegistrationInd: false,
	blinkQRCode: {
		imageFormat: '',
		imageUri: '',
		referenceId: '',
	},
	bigMacLink: '',
	saveModalMsg: '',
	showSaveModal: false,
	userHasUnpublishedVideos: false,
	hasSocialVAccount: false,
	addresses: [],
	isPilotUser: false,
};

const mutations = {
	SET_USER_PROFILE(state, payload) {
		const { contactLicenses } = payload;
		if (Array.isArray(contactLicenses)) {
			payload.contactLicenses = mapDreForStore(contactLicenses);
		}
		Object.assign(state, payload);
	},
	UPDATE_USER_PROFILE_DETAILS(state, data) {
		// TODO: Remove or refactor. This mutation is only being called for bigMacLink which isn't being used.
		state[data.fieldName] = data.value;
		if (
			this.state.licenses.disclaimerTextSelected &&
			(data.fieldName === 'firstName' ||
				data.fieldName === 'lastName' ||
				data.fieldName === 'phoneNumber' ||
				data.fieldName === 'phoneExtension' ||
				data.fieldName === 'addressLine1' ||
				data.fieldName === 'addressLine2' ||
				data.fieldName === 'city' ||
				data.fieldName === 'state' ||
				data.fieldName === 'zip')
		) {
			state.showDisclaimerNotification = true;
		}
		if (
			this.state.licenses.disclaimerTextSelected &&
			(data.fieldName === 'firstName' ||
				data.fieldName === 'lastName' ||
				data.fieldName === 'middleName' ||
				data.fieldName === 'preferredName')
		) {
			state.saveUserInCCV2 = true;
		}
	},
	UPDATE_USER_TERMS_OF_USE_ACCEPTED(state) {
		state.termsOfUse = true;
	},

	SET_USER_AS_ACCOUNT_EXECUTIVE(state) {
		state.isAccountExecutive = true;
	},

	SET_TERMS_FROM_PROFILE_SELECTOR(state, profileSelectorData) {
		if (profileSelectorData.isInternalUser) {
			state.termsOfUse = true;
		}
	},

	USERPROFILE_ISEDITED(state, value = false) {
		if (value === false) {
			state.showDisclaimerNotification = false;
		}
	},
	RESET_USER_ROLODEX_MODIFIED_FLAG(state) {
		state.rolodexModifiedIndicator = false;
	},

	RESET_SAVEUSERINCCV2_FLAG(state) {
		state.saveUserInCCV2 = false;
	},

	SHOW_SAVE_MODAL(
		state,
		{ showModal, msg, title } = { showModal: false, msg: '', title: '' },
	) {
		state.saveModalTitle = title;
		state.saveModalMsg = msg;
		state.showSaveModal = showModal;
	},
	SAVE_FAMB_SETTINGS_SUCCESS(state, payload) {
		const { fambYourProfileURLPath, fambYourProfileURLSlug } = payload;
		state.fambYourProfileURL = fambYourProfileURLPath + fambYourProfileURLSlug;
	},
	SET_USER_HAS_UNPUBLISHED_VIDEOS(state, data) {
		state.userHasUnpublishedVideos = data;
	},
	SET_HAS_SOCIAL_V_ACCOUNT(state, hasSocialVAccount) {
		state.hasSocialVAccount = hasSocialVAccount;
	},
	REMOVE_ADDITIONAL_ADDRESS(state, displayOrder) {
		state.addresses = state.addresses.filter(
			(address) => address.displayOrder !== displayOrder,
		);
	},
	SET_INITIAL_ADDRESSES_ORDER(state) {
		//addresses will have a order with the object, place in that order
		if (state.addresses.length > 0) {
			state.addresses.sort((a, b) => a.displayOrder - b.displayOrder);
		}
	},
	SET_ADDRESS_ORDER(state) {
		//after deleting address, resort and re-assign display order so it is correct
		let i = 0;
		state.addresses.sort((a, b) => a.displayOrder - b.displayOrder);
		for (let address of state.addresses) {
			address.displayOrder = i;
			i++;
		}
	},
};

const actions = {
	/* in all actions context is an object that represents the store instance.
	 * for more information see https://vuex.vuejs.org/guide/actions.html */
	setUserProfile({ commit }, payload) {
		/* This if statement is required to display the textarea; will not display if null. */
		if (payload.aboutLO === null) {
			payload.aboutLO = '';
		}
		commit('SET_USER_PROFILE', payload);
	},
	updateUserTermsOfUseAccepted({ commit }) {
		commit('UPDATE_USER_TERMS_OF_USE_ACCEPTED');
	},

	setAccountExecutive({ commit }, token) {
		if (token !== null && token.access_token !== null) {
			const encoded_token = token.access_token
				.split('.')[1]
				.replace('-', '+')
				.replace('_', '/');
			const token_object = JSON.parse(atob(encoded_token));
			if (
				token_object.isAEMember !== undefined &&
				token_object.isAEMember !== null
			) {
				commit('SET_USER_AS_ACCOUNT_EXECUTIVE');
			}
		}
	},

	setTermsFromProfileSelector({ commit }, profileSelectorData) {
		commit('SET_TERMS_FROM_PROFILE_SELECTOR', profileSelectorData);
	},

	async saveUserProfile(
		{ commit, dispatch, getters, rootState, state },
		profile,
	) {
		try {
			const states = profile.addresses.map(({ state }) => state);

			const duplicateStateAddresses = states.filter((state, index) =>
				states.some((item, idx) => state === item && idx !== index),
			);
			//INFO: work around for same state addresses - won't work in form validation - B360-3959
			if (duplicateStateAddresses.length > 0) {
				dispatch('showErrorModal', {
					errorDetails: ERROR_DETAILS.SAVE_DUPLICATE_STATE,
				});
				return Promise.reject(new Error(ERROR_DETAILS.SAVE_DUPLICATE_STATE));
			}
			const { rolodexEntityId } = rootState.companyProfile;
			const { contactLicenses } = profile;
			const payload = { ...profile };

			/** Convert contactLicenses back to an array */
			payload.contactLicenses = mapDreForSave(contactLicenses);
			payload.rolodexEntityId = rolodexEntityId;
			payload.isAddressUpdated = state.addresses[0] !== profile.addresses[0];

			const { saveFambResponse, saveUserResponse } =
				await profileApiClient.saveUser(payload);
			if (saveUserResponse === true) {
				/** Determine if the disclaimer notification modal and PreferenceCenterUser should appear. */
				const contactInfoProperties = [
					'firstName',
					'lastName',
					'phoneNumber',
					'phoneExtension',
					'addressLine1',
					'addressLine2',
					'city',
					'state',
					'zip',
				];
				const contactInfoChanged = isDifferent(
					contactInfoProperties,
					profile,
					state,
				);
				if (rootState.licenses.disclaimerTextSelected && contactInfoChanged) {
					profile.showDisclaimerNotification = true;
				} else {
					profile.showDisclaimerNotification = false;
				}

				/** Ultimately saveUserInCCV2 results in /Lead/SaveOwner being called. */
				const nameProperties = [
					'firstName',
					'lastName',
					'middleName',
					'preferredName',
				];
				const nameInfoChanged = isDifferent(nameProperties, profile, state);
				if (rootState.licenses.disclaimerTextSelected && nameInfoChanged) {
					profile.saveUserInCCV2 = true;
				} else {
					profile.saveUserInCCV2 = false;
				}
				commit('SET_USER_PROFILE', profile);

				if (saveFambResponse === false && getters.hasAccessToFamb === true) {
					dispatch('showErrorModal', {
						errorDetails: ERROR_DETAILS.SAVE_USER_FAMB,
					});
				}
				return Promise.resolve(true);
			} else {
				dispatch('showErrorModal', {
					errorDetails: ERROR_DETAILS.SAVE_USER,
				});
				return Promise.reject(new Error(ERROR_DETAILS.SAVE_USER));
			}
		} catch (error) {
			dispatch('showErrorModal', {
				error,
				errorDetails: ERROR_DETAILS.SAVE_USER,
			});
			return Promise.reject(error instanceof Error ? error : new Error(error));
		}
	},
	async resetUserRolodexModified({ commit, state }) {
		try {
			const data = await profileApiClient.resetUserRolodexModifiedFlag(
				state.userId,
			);
			return data;
		} catch {
			return false;
		} finally {
			commit('RESET_USER_ROLODEX_MODIFIED_FLAG');
		}
	},
	async resetSaveUserInCCV2Flag({ commit, state }) {
		commit('RESET_SAVEUSERINCCV2_FLAG');
	},
	async generateVendorLink({ commit, state, rootGetters }) {
		try {
			const rolodexEntityId = rootGetters.currentUserProfile.entityId;
			const rolodexContactId = rootGetters.currentUserProfile.rolodexContactId;
			const { contactLicenses } = state;
			const payload = {
				...state,
				rolodexEntityId,
				rolodexContactId,
			};

			payload.contactLicenses = mapDreForSave(contactLicenses);
			const { url, status } = await profileApiClient.getVendorLink(payload);

			if (!!url && status) {
				commit('UPDATE_USER_PROFILE_DETAILS', {
					fieldName: 'bigMacLink',
					value: url,
				});
				commit('SHOW_SAVE_MODAL', {
					showModal: true,
					msg: USER_PROFILE_RESPONSES.VENDOR_LINK_SUCCESS,
					title: MODAL_TITLES.VENDOR_LINK_TITLE,
				});
			} else {
				commit('SHOW_SAVE_MODAL', {
					showModal: true,
					msg: USER_PROFILE_RESPONSES.VENDOR_LINK_ERROR,
					title: MODAL_TITLES.VENDOR_LINK_TITLE,
				});
			}
		} catch (e) {
			return Promise.reject(e);
		}
	},
	async getUserHasVideoReviews({ commit, state }) {
		try {
			const payload = {
				userId: state.userId,
				rolodexEntityId: rootState.companyProfile.rolodexEntityId,
				rolodexContactId: state.rolodexContactId,
			};
			const data = await profileApiClient.getUserHasVideoReviews(payload);
			commit('SET_USER_HAS_UNPUBLISHED_VIDEOS', data);
			return Promise.resolve(data);
		} catch (error) {
			commit('SET_USER_HAS_UNPUBLISHED_VIDEOS', false);
			return Promise.resolve({
				error: 'there was an error',
			});
		}
	},
	setUserHasVideoReviews({ commit }, data) {
		commit('SET_USER_HAS_UNPUBLISHED_VIDEOS', data);
	},
	async getADFSLink(
		{ dispatch, state, rootState },
		accountActivationData = {},
	) {
		try {
			const payload = {
				rolodexContactId: state.rolodexContactId,
				rolodexEntityId: rootState.companyProfile.rolodexEntityId,
				userId: state.userId,
				hasSocialVAccount: state.hasSocialVAccount,
			};
			if (accountActivationData) {
				payload.exportRecentLoanLeadData =
					accountActivationData.exportRecentLoanLeadData;
				payload.DBA = accountActivationData.companyName;
			}

			const adfsLink = await profileApiClient.getADFSLink(payload);
			// open the link in a new tab
			window.open(adfsLink, '_blank');
			dispatch('setHasSocialVAccount', true);
			return Promise.resolve('success');
		} catch (error) {
			dispatch('showErrorModal', {
				error,
				errorDetails: ERROR_DETAILS.VIDEO_REVIEW_ERROR,
			});

			return Promise.reject(new Error('fail'));
		} finally {
			dispatch('setVideoReviewSpinner', false);
		}
	},
	setHasSocialVAccount({ commit }, hasSocialVAccount) {
		commit('SET_HAS_SOCIAL_V_ACCOUNT', hasSocialVAccount);
	},
	canAccessSocialV(state) {
		const role = 'LoanOfficer';
		return state.roles.includes(role);
	},
	async updateSocialVAccount(context, payload) {
		try {
			const data = await profileApiClient.updateSocialVAccount(payload);
			return Promise.resolve(data);
		} catch (error) {
			return Promise.resolve({
				error: 'there was an error',
			});
		}
	},
	removeAdditionalAddress({ commit }, displayOrder) {
		commit('REMOVE_ADDITIONAL_ADDRESS', displayOrder);
	},
	setInitialAddresses({ commit }) {
		commit('SET_INITIAL_ADDRESSES_ORDER');
	},
	setAddressOrder({ commit }) {
		commit('SET_ADDRESS_ORDER');
	},
	async deleteAddress({ commit }, payload) {
		try {
			const response = await profileApiClient.deleteAddress(payload);
			return Promise.resolve(response);
		} catch (error) {
			Promise.reject(error);
		}
	},
};

const getters = {
	/*
	The userId is set to 0 by default in the store.
	If the profile has loaded the userId property will not be 0.
	The userId should also never be negative.
	*/
	isUserActive(state) {
		return () => state.userId > 0;
	},
	/*
	isBrandItem takes a brand item name and checks against the state.
	@param name, this is the name of the brand item being checked (ex. 'Color1', 'HeaderFontFamily')
	@return bool, if brand is not an array return FALSE. if state.brand is an
	array find the index of the item checking by name.
	*/
	isBrandItem(state) {
		return (name) =>
			Array.isArray(state.brand) &&
			state.brand.findIndex((el) => el.name === name) >= 0;
	},
	/*
	isSocialItem takes a brand item name and checks against the state.
	@param type, this is the type/name of the social item being checked (ex. 'FaceBook', 'Instagram')
	@return bool, if brand is not an array return FALSE. if state.socialMediaDetails is an
	array find the index of the item checking by type.
	*/
	isSocialItem(state) {
		return (type) =>
			Array.isArray(state.socialMediaDetails) &&
			state.socialMediaDetails.findIndex((el) => el.type === type) >= 0;
	},
	/*
	Derives the user color palette from the brand profile.
	Used by Brand Builder Editor.
	@return object, color palette object.
*/
	userColorPalette(state) {
		return setCSSCustomUserOrCompanyColors(state.brand, 'User Colors');
	},
	/*
		If the profile user is not the same as the one in profileSwitcher mainProfile,
		then this user is being impersonated.
		If profileSwitcher mainProfile hasn't been set yet (contactId === 0) the app is
		still loading and user isn't impersonating (yet).
		@return bool
	*/
	isImpersonated(state, getters, rootState, rootGetters) {
		return rootState.profileSwitcher.mainProfile.contactId === null
			? false
			: state.contactId !== rootState.profileSwitcher.mainProfile.contactId;
	},
	/*
		True if user has both profile and company profile Blink Registration Indicators
		@return bool
	*/
	hasBlink(state, getters, rootState) {
		return (
			state.blinkRegistrationInd &&
			rootState.companyProfile.blinkRegistrationInd
		);
	},
	canCreateSocialVAccount(state, getters, rootState) {
		const isInternalUser =
			rootState.profileSwitcher.mainProfileSelector.isInternalUser;
		const isLoanOfficer = state.roles.includes('LoanOfficer');

		return !isInternalUser && isLoanOfficer;
	},
};

export default {
	state,
	actions,
	mutations,
	getters,
};
